import { useMemo, useState } from 'react';
import { Account, ClosedPosition, OpenPosition } from '@monorepo/types';
import { useStore } from '@hub/helpers/use-store';
import { useQuery } from '@tanstack/react-query';
import { PositionViewType, UseTradesTableDataProps } from '../../types';
import { shouldDisablePolling } from '../../utils/polling-utils';
import { processPositionsData } from '../../utils/position-data-processor';
import {
  DEFAULT_POSITIONS_LIMIT,
  DEFAULT_POSITIONS_PAGE,
  TRADES_POLLING_INTERVAL,
  TRADES_STALE_TIME,
} from '../constants';
import {
  createAllPositionsQuery,
  createClosedPositionsQuery,
  createOpenPositionsQuery,
} from '../query-creators';

export const useTradesTableData = ({
  accountId,
  account: fullAccount,
  page = DEFAULT_POSITIONS_PAGE,
  limit = DEFAULT_POSITIONS_LIMIT,
}: UseTradesTableDataProps) => {
  const [selectedViews, setSelectedViews] = useState<PositionViewType>(
    PositionViewType.BOTH,
  );

  const {
    dataStore: { positionStore },
  } = useStore();

  // Create mock account object for query creators or use provided account
  const account = useMemo(
    () => fullAccount || ({ tsAccountId: accountId } as Account),
    [accountId, fullAccount],
  );

  // Check if account polling should be disabled
  const isPollingDisabled = shouldDisablePolling(fullAccount);

  const isBoth = selectedViews === PositionViewType.BOTH;

  // Use individual useQuery hooks for better pagination control
  const closedQuery = useQuery<{ results: ClosedPosition[]; total: number }>({
    ...createClosedPositionsQuery(
      positionStore,
      account,
      page,
      limit,
      selectedViews === PositionViewType.CLOSED,
    ),
    refetchInterval: isPollingDisabled ? false : TRADES_POLLING_INTERVAL,
    refetchIntervalInBackground: false,
    staleTime: TRADES_STALE_TIME,
    placeholderData: (previousData) => previousData,
  });

  const openQuery = useQuery<{ results: OpenPosition[]; total: number }>({
    ...createOpenPositionsQuery(
      positionStore,
      account,
      page,
      limit,
      selectedViews === PositionViewType.OPEN,
    ),
    refetchInterval: isPollingDisabled ? false : TRADES_POLLING_INTERVAL,
    refetchIntervalInBackground: false,
    staleTime: TRADES_STALE_TIME,
    placeholderData: (previousData) => previousData,
  });

  const allQuery = useQuery<{
    results: (OpenPosition | ClosedPosition)[];
    total: number;
  }>({
    ...createAllPositionsQuery(positionStore, account, page, limit, isBoth),
    refetchInterval: isPollingDisabled ? false : TRADES_POLLING_INTERVAL,
    refetchIntervalInBackground: false,
    staleTime: TRADES_STALE_TIME,
    placeholderData: (previousData) => previousData,
  });

  // Extract query results with better type safety
  const closedData = closedQuery.data;
  const openData = openQuery.data;
  const allData = allQuery.data;
  const isLoadingClosed = closedQuery.isLoading;
  const isLoadingOpen = openQuery.isLoading;
  const isLoadingAll = allQuery.isLoading;
  const closedError = closedQuery.error;
  const openError = openQuery.error;
  const allError = allQuery.error;

  // Process positions data using pure function for better testability
  const { positions, isLoading, totalCount } = useMemo(() => {
    if (isBoth) {
      const data = allData?.results || [];
      const total = allData?.total || 0;
      const loading = data.length === 0 && isLoadingAll;
      return { positions: data, isLoading: loading, totalCount: total };
    }
    return processPositionsData(
      closedData,
      openData,
      selectedViews,
      isLoadingClosed,
      isLoadingOpen,
    );
  }, [
    isBoth,
    allData,
    isLoadingAll,
    closedData,
    openData,
    selectedViews,
    isLoadingClosed,
    isLoadingOpen,
  ]);

  // Helper function for refetching data based on selected views
  const handleRefetch = () => {
    if (isBoth) {
      allQuery.refetch();
      return;
    }

    const refetchActions = [
      {
        condition: selectedViews === PositionViewType.CLOSED,
        action: () => closedQuery.refetch(),
      },
      {
        condition: selectedViews === PositionViewType.OPEN,
        action: () => openQuery.refetch(),
      },
    ];

    refetchActions
      .filter(({ condition }) => condition)
      .forEach(({ action }) => action());
  };

  return {
    selectedViews,
    setSelectedViews,
    positions,
    isLoading,
    totalCount,
    errors: {
      closedError,
      openError,
      allError,
    },
    refetch: handleRefetch,
  };
};
